import { jsx } from 'react/jsx-runtime';
import { styled, css } from '@flex-design-system/stitches-react';
import { forwardRef } from 'react';
import { Typography } from '../../../typography/Typography.mjs';
import { SELECTED_DAY_BACKGROUND_COLOR_CSS_VARIABLE, DAY_HOVER_BACKGROUND_COLOR_CSS_VARIABLE } from '../../constants/styles.mjs';

const DayText = forwardRef(
  ({ dayOfMonth, ...props }, ref) => {
    return /* @__PURE__ */ jsx(StyledText, { ref, weight: 500, ...props, children: dayOfMonth });
  }
);
const StyledText = styled(Typography);
const dayVariants = {
  common: {
    backgroundColor: "$white",
    color: "$grayDark"
  },
  commonHover: {
    backgroundColor: `var(${DAY_HOVER_BACKGROUND_COLOR_CSS_VARIABLE})`,
    color: "$grayDark"
  },
  today: {
    [`${StyledText}`]: {
      fontWeight: 700,
      color: "$primary"
    }
  },
  todayHover: {},
  selected: {
    backgroundColor: "$green",
    color: "$white",
    boxShadow: "$datePickerSelected",
    [`${StyledText}`]: {
      fontWeight: 600,
      color: "$white"
    }
  },
  selectedHover: {
    backgroundColor: "$green",
    color: "$white"
  },
  keyboardSelected: {
    backgroundColor: "$blackAlpha003",
    color: "$grayDark"
  },
  keyboardSelectedHover: {
    backgroundColor: `var(${DAY_HOVER_BACKGROUND_COLOR_CSS_VARIABLE})`,
    color: "$grayDark"
  },
  outsideOfMonth: {
    color: "$blackAlpha040",
    boxShadow: "none"
  },
  outsideOfMonthHover: {
    backgroundColor: `var(${DAY_HOVER_BACKGROUND_COLOR_CSS_VARIABLE})`,
    color: "$blackAlpha040"
  },
  outsideOfMonthAndSelected: {
    backgroundColor: `var(${SELECTED_DAY_BACKGROUND_COLOR_CSS_VARIABLE})`,
    color: "$blackAlpha040"
  },
  disabled: {
    color: "$blackAlpha040",
    backgroundColor: "$white",
    cursor: "not-allowed",
    boxShadow: "none",
    [`${StyledText}`]: {
      fontWeight: 500
    }
  },
  selectedAndDisabled: {
    color: "$white",
    backgroundColor: "$green",
    opacity: 0.6
  },
  disabledHover: {
    color: "$blackAlpha040",
    backgroundColor: "$white",
    [`${StyledText}`]: {
      fontWeight: 500
    }
  },
  // Range
  rangeCommon: {
    backgroundColor: `var(${SELECTED_DAY_BACKGROUND_COLOR_CSS_VARIABLE})`,
    color: "$greenDarker",
    borderRadius: 0,
    [`${StyledText}`]: {
      fontWeight: 500
    }
  },
  rangeCommonHover: {
    color: "$greenDarker"
  },
  rangeDisabled: {
    backgroundColor: `var(${SELECTED_DAY_BACKGROUND_COLOR_CSS_VARIABLE})`,
    opacity: 0.6
  },
  rangeStart: {
    borderTopLeftRadius: "5px",
    borderBottomLeftRadius: "5px"
  },
  rangeEnd: {
    borderTopRightRadius: "5px",
    borderBottomRightRadius: "5px"
  }};
const dayOverridingStyles = css({
  [SELECTED_DAY_BACKGROUND_COLOR_CSS_VARIABLE]: "#E5FAE8",
  [DAY_HOVER_BACKGROUND_COLOR_CSS_VARIABLE]: "$colors$blackAlpha003",
  "&.react-datepicker__day": {
    outline: "none !important",
    margin: "0px",
    width: "100%",
    minWidth: "32px",
    lineHeight: "32px",
    borderRadius: "5px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    ...dayVariants.common,
    "&:hover": {
      ...dayVariants.commonHover
    },
    "&.react-datepicker__day--today": {
      ...dayVariants.today,
      "&:hover": {
        ...dayVariants.todayHover
      }
    },
    "&.react-datepicker__day--keyboard-selected": {
      ...dayVariants.keyboardSelected,
      "&:hover": {
        ...dayVariants.keyboardSelectedHover
      }
    },
    "&.react-datepicker__day--selected": {
      ...dayVariants.selected,
      "&:hover": {
        ...dayVariants.selectedHover
      }
    },
    "&.react-datepicker__day--outside-month": {
      "&:not(.react-datepicker__day--in-range)": {
        ...dayVariants.outsideOfMonth,
        "&:hover": {
          ...dayVariants.outsideOfMonthHover
        },
        "&.react-datepicker__day--selected": {
          ...dayVariants.outsideOfMonthAndSelected
        }
      }
    },
    // Range
    "&.react-datepicker__day--in-range, &.react-datepicker__day--in-selecting-range": {
      ...dayVariants.rangeCommon,
      "&:hover": {
        ...dayVariants.rangeCommonHover
      }
    },
    "&.react-datepicker__day--range-start, &.react-datepicker__day--selecting-range-start": {
      ...dayVariants.selected,
      ...dayVariants.rangeStart,
      "&:hover": {
        ...dayVariants.selectedHover
      }
    },
    "&.react-datepicker__day--range-end, &.react-datepicker__day--selecting-range-end": {
      ...dayVariants.selected,
      ...dayVariants.rangeEnd,
      "&:hover": {
        ...dayVariants.selectedHover
      }
    },
    "&:not(.react-datepicker__day--disabled)": {
      cursor: "pointer"
    },
    "&.react-datepicker__day--disabled": {
      ...dayVariants.disabled,
      "&:hover": {
        ...dayVariants.disabledHover
      }
    },
    // 선택할 수 없는 날짜를 선택한 경우
    "&.react-datepicker__day--selected.react-datepicker__day--disabled": {
      ...dayVariants.selectedAndDisabled
    },
    // 선택할 수 없는 기간의 시작일 선택한 경우
    "&.react-datepicker__day--range-start.react-datepicker__day--disabled": {
      ...dayVariants.selectedAndDisabled
    },
    // 선택할 수 없는 기간을 선택한 경우(시작, 종료일 제외)
    "&.react-datepicker__day--in-range.react-datepicker__day--disabled": {
      "&:not(.react-datepicker__day--range-start, .react-datepicker__day--range-end)": {
        ...dayVariants.rangeDisabled
      }
    },
    // 선택할 수 없는 기간의 종료일을 선택한 경우
    "&.react-datepicker__day--range-end.react-datepicker__day--disabled": {
      ...dayVariants.selectedAndDisabled
    }
  }
})();

export { DayText, dayOverridingStyles, dayVariants };
//# sourceMappingURL=day.mjs.map
